/**
 * Hook for monitoring component health in real-time
 *
 * Provides real-time health status for all local AI components
 * via WebSocket subscription with fallback to polling.
 */

import { useCallback, useEffect, useState, useRef } from 'react';

import {
  getAllComponentHealth,
  subscribeToHealthUpdates,
  startComponent,
  stopComponent,
} from '../services/localFirstService';
import type { ComponentHealth } from '../services/localFirstService';
import { FAILURE_THRESHOLDS, POLLING_INTERVALS } from '../constants/timings';

import { handleError } from '../utils/errorHandler';

export interface UseComponentHealthResult {
  /** Health status for all components, keyed by component ID */
  health: Record<string, ComponentHealth>;
  /** Whether the health data is currently being loaded */
  loading: boolean;
  /** Error message if loading failed */
  error: string | null;
  /** Manually refresh health data from the server */
  refresh: () => Promise<void>;
  /** Start a specific component */
  start: (componentId: string) => Promise<boolean>;
  /** Stop a specific component */
  stop: (componentId: string) => Promise<boolean>;
}

/**
 * Hook to monitor component health status in real-time
 *
 * Automatically subscribes to WebSocket updates and falls back to
 * polling if WebSocket connection fails.
 *
 * @example
 * ```tsx
 * function HealthDashboard() {
 *   const { health, loading, start, stop } = useComponentHealth();
 *
 *   if (loading) return <Spinner />;
 *
 *   return (
 *     <div>
 *       {Object.values(health).map(component => (
 *         <HealthCard
 *           key={component.componentId}
 *           component={component}
 *           onStart={() => start(component.componentId)}
 *           onStop={() => stop(component.componentId)}
 *         />
 *       ))}
 *     </div>
 *   );
 * }
 * ```
 */
export function useComponentHealth(): UseComponentHealthResult {
  const [health, setHealth] = useState<Record<string, ComponentHealth>>({});
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const pollingIntervalRef = useRef<ReturnType<typeof setInterval> | null>(null);
  const wsUnsubscribeRef = useRef<(() => void) | null>(null);
  const failureCountRef = useRef(0);

  // Configuration for polling behavior
  const MAX_FAILURES_BEFORE_STOP = FAILURE_THRESHOLDS.STOP_AFTER_FAILURES;
  const POLLING_INTERVAL_MS = POLLING_INTERVALS.HEALTH_CHECK;

  const fetchHealth = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);
      const data = await getAllComponentHealth();
      setHealth(data);
      failureCountRef.current = 0; // Reset on success
    } catch (e) {
      console.error('[useComponentHealth] Failed to fetch health:', e);
      setError(handleError(e, 'ComponentHealth', 'Failed to fetch health status'));

      // Track failures to avoid endless polling when endpoint doesn't exist
      failureCountRef.current++;
      if (failureCountRef.current >= MAX_FAILURES_BEFORE_STOP) {
        console.log(
          '[useComponentHealth] Health endpoint unavailable after multiple attempts. ' +
            'Stopping polling to conserve resources.'
        );
        if (pollingIntervalRef.current) {
          clearInterval(pollingIntervalRef.current);
          pollingIntervalRef.current = null;
        }
      }
    } finally {
      setLoading(false);
    }
  }, [MAX_FAILURES_BEFORE_STOP]);

  useEffect(() => {
    // Reset failure count on mount
    failureCountRef.current = 0;

    // Initial fetch
    fetchHealth();

    // Try WebSocket subscription
    const unsubscribe = subscribeToHealthUpdates(
      // On individual health update
      (componentId, componentHealth) => {
        setHealth((prev) => ({
          ...prev,
          [componentId]: componentHealth,
        }));
      },
      // On initial health state
      (initialHealth) => {
        setHealth(initialHealth);
        setLoading(false);
      }
    );
    wsUnsubscribeRef.current = unsubscribe;

    // Fallback to polling every 30 seconds (will stop after MAX_FAILURES_BEFORE_STOP failures)
    pollingIntervalRef.current = setInterval(() => {
      // Only poll if we haven't exceeded failure limit
      if (failureCountRef.current < MAX_FAILURES_BEFORE_STOP) {
        fetchHealth();
      }
    }, POLLING_INTERVAL_MS);

    // Cleanup
    return () => {
      if (wsUnsubscribeRef.current) {
        wsUnsubscribeRef.current();
      }
      if (pollingIntervalRef.current) {
        clearInterval(pollingIntervalRef.current);
      }
    };
  }, [fetchHealth, MAX_FAILURES_BEFORE_STOP, POLLING_INTERVAL_MS]);

  const start = useCallback(async (componentId: string): Promise<boolean> => {
    try {
      const success = await startComponent(componentId);
      if (success) {
        // Optimistically update status
        setHealth((prev) => {
          const existing = prev[componentId];
          if (!existing) return prev;
          return {
            ...prev,
            [componentId]: {
              ...existing,
              isRunning: true,
              status: 'healthy' as const,
            },
          };
        });
      }
      return success;
    } catch (e) {
      console.error(`[useComponentHealth] Failed to start ${componentId}:`, e);
      return false;
    }
  }, []);

  const stop = useCallback(async (componentId: string): Promise<boolean> => {
    try {
      const success = await stopComponent(componentId);
      if (success) {
        // Optimistically update status
        setHealth((prev) => {
          const existing = prev[componentId];
          if (!existing) return prev;
          return {
            ...prev,
            [componentId]: {
              ...existing,
              isRunning: false,
              status: 'unknown' as const,
            },
          };
        });
      }
      return success;
    } catch (e) {
      console.error(`[useComponentHealth] Failed to stop ${componentId}:`, e);
      return false;
    }
  }, []);

  return {
    health,
    loading,
    error,
    refresh: fetchHealth,
    start,
    stop,
  };
}
